import { getRequiredEntityAlias } from "./entity-aliases.js";
import { evaluateCollectionMembership } from "./filter-evaluator.js";
import { resolveParentValues } from "./filter-function-registry.js";
import { getRootFolderEntityTypeKeys, requireCompiledProjectTreeFolderKind, } from "./root-folders.js";
import { resolveEntityRouteFromTreeSelection } from "./routes/index.js";
import { isSolutionModelFrameworkKey } from "./solution-model-frameworks.js";
export function resolveFrameworkFolderLocation(input) {
    if (!input.rootNode) {
        return null;
    }
    const segments = input.folderPath.split("/").filter(Boolean);
    if (segments.length === 0) {
        return { kind: "root", node: input.rootNode };
    }
    let parentKey = input.rootNode.key;
    let matchedNode = null;
    for (let index = 0; index < segments.length; index += 1) {
        const segment = segments[index];
        const childNode = input.folderNodes.find((node) => node.parentKey === parentKey &&
            node.slug === segment &&
            (node.kind === "folder" || node.kind === "collection")) ?? null;
        if (!childNode) {
            return null;
        }
        matchedNode = childNode;
        if (childNode.kind === "collection") {
            return index === segments.length - 1 ? { kind: "collection", node: childNode } : null;
        }
        parentKey = childNode.key;
    }
    return matchedNode?.kind === "folder" ? { kind: "folder", node: matchedNode } : null;
}
function buildEntityNavigationKey(entityTypeKey, entitySlug) {
    return `${entityTypeKey}:${entitySlug}`;
}
function appendOccurrence(map, key, occurrence) {
    const existing = map.get(key) ?? [];
    if (existing.some((entry) => entry.path === occurrence.path)) {
        return;
    }
    map.set(key, [...existing, occurrence]);
}
function recordOccurrencePath(input) {
    const occurrence = {
        entityId: input.entityId,
        entityTypeKey: input.entityTypeKey,
        entitySlug: input.entitySlug,
        path: input.path,
        order: input.order,
        rootFolderKey: input.rootFolderKey,
        rootFolderSlug: input.rootFolderSlug,
        folderSlugs: input.folderSlugs,
        lineage: input.lineage,
    };
    appendOccurrence(input.index.occurrencesByEntityId, input.entityId, occurrence);
    appendOccurrence(input.index.occurrencesByEntityKey, buildEntityNavigationKey(input.entityTypeKey, input.entitySlug), occurrence);
}
function getCanonicalRootFolderKeyByEntityType(structure) {
    const canonicalRootFolderKeyByEntityType = new Map();
    for (const entityDisplay of structure?.entityDisplays ?? []) {
        if (entityDisplay.entityTypeKey && entityDisplay.canonicalRootFolderKey) {
            canonicalRootFolderKeyByEntityType.set(entityDisplay.entityTypeKey, entityDisplay.canonicalRootFolderKey);
        }
    }
    return canonicalRootFolderKeyByEntityType;
}
function selectCanonicalOccurrence(occurrences, canonicalRootFolderKey) {
    return [...occurrences].sort((left, right) => {
        const leftMatchesCanonical = canonicalRootFolderKey
            ? left.rootFolderKey === canonicalRootFolderKey
            : false;
        const rightMatchesCanonical = canonicalRootFolderKey
            ? right.rootFolderKey === canonicalRootFolderKey
            : false;
        if (leftMatchesCanonical !== rightMatchesCanonical) {
            return leftMatchesCanonical ? -1 : 1;
        }
        const scopedDelta = right.folderSlugs.length - left.folderSlugs.length;
        if (scopedDelta !== 0) {
            return scopedDelta;
        }
        const lineageDelta = right.lineage.length - left.lineage.length;
        if (lineageDelta !== 0) {
            return lineageDelta;
        }
        return left.order - right.order;
    })[0];
}
function finalizeCanonicalEntries(index, canonicalRootFolderKeyByEntityType) {
    for (const [entityId, occurrences] of index.occurrencesByEntityId.entries()) {
        const canonical = selectCanonicalOccurrence(occurrences, canonicalRootFolderKeyByEntityType.get(occurrences[0]?.entityTypeKey ?? ""));
        if (!canonical)
            continue;
        index.byEntityId.set(entityId, canonical);
    }
    for (const [entityKey, occurrences] of index.occurrencesByEntityKey.entries()) {
        const canonical = selectCanonicalOccurrence(occurrences, canonicalRootFolderKeyByEntityType.get(occurrences[0]?.entityTypeKey ?? ""));
        if (!canonical)
            continue;
        index.byEntityKey.set(entityKey, canonical);
    }
}
function getCollectionNodeSpecificity(node) {
    const fromParentScore = node.fromParent ? 1 : 0;
    const filter = node.filter;
    if (!filter) {
        return fromParentScore;
    }
    if (filter.op === "eq") {
        return fromParentScore + 1;
    }
    if (filter.op === "and" && filter.nodes.every((child) => child.op === "eq")) {
        return fromParentScore + filter.nodes.length;
    }
    return fromParentScore;
}
function resolveProjectTreeCollectionEntities(input) {
    const entityTypeKey = input.collectionNode.entityTypeKey;
    if (!entityTypeKey) {
        return [];
    }
    const lineageForParents = input.lineage.map((entry) => ({
        entityTypeKey: entry.entity._entityTypeKey,
        entityId: entry.entity._id,
        entitySlug: entry.entity.slug,
    }));
    const { parentValues, missingRequired } = resolveParentValues(input.collectionNode.fromParent, lineageForParents);
    if (missingRequired.length > 0) {
        return [];
    }
    const matching = input.entities.filter((entity) => {
        if (entity._entityTypeKey !== entityTypeKey) {
            return false;
        }
        return evaluateCollectionMembership({
            entity: entity,
            filter: input.collectionNode.filter,
            filterFnKey: input.collectionNode.filterFnKey,
            parentValues,
            pathScope: {
                rootFolderKey: input.rootFolderKey,
                rootFolderSlug: input.rootFolderSlug,
                folderNodeKeys: [
                    ...input.lineage.map((entry) => entry.collectionNodeKey),
                    input.collectionNode.key,
                ],
                folderSlugs: [input.folder.slug, input.rootCollectionSlug],
                projectTreeFolderId: input.folder._id,
                projectTreeFolderSlug: input.folder.slug,
                entityLineage: input.lineage.map((entry) => ({
                    entityTypeKey: entry.entity._entityTypeKey,
                    entityId: entry.entity._id,
                    entitySlug: entry.entity.slug,
                    nodeKey: entry.collectionNodeKey,
                })),
            },
        });
    });
    return matching;
}
function addScopedEntityCandidates(input) {
    const lineageForPath = input.lineage.map((entry) => ({
        entityTypeKey: entry.entity._entityTypeKey,
        entitySlug: entry.entity.slug,
    }));
    recordOccurrencePath({
        index: input.index,
        entityId: input.entity._id,
        entityTypeKey: input.entity._entityTypeKey,
        entitySlug: input.entity.slug,
        path: resolveEntityRouteFromTreeSelection({
            rootFolderSlug: input.rootFolderSlug,
            entityTypeKey: input.entity._entityTypeKey,
            entitySlug: input.entity.slug,
            aliases: input.aliases,
            folderSlugs: [input.folder.slug],
            entityLineage: lineageForPath,
        }),
        order: input.orderRef.current++,
        rootFolderKey: input.rootFolderKey,
        rootFolderSlug: input.rootFolderSlug,
        folderSlugs: [input.folder.slug],
        lineage: lineageForPath,
    });
    const nextLineage = [
        ...input.lineage,
        { entity: input.entity, collectionNodeKey: input.collectionNode.key },
    ];
    const childCollectionNodes = input.collectionNodes
        .filter((node) => node.parentKey === input.collectionNode.key)
        .sort((left, right) => left.listOrder - right.listOrder);
    const visitedCollectionNodeKeys = new Set(input.visitedCollectionNodeKeys ?? []);
    visitedCollectionNodeKeys.add(input.collectionNode.key);
    for (const childCollectionNode of childCollectionNodes) {
        if (visitedCollectionNodeKeys.has(childCollectionNode.key))
            continue;
        const childEntities = resolveProjectTreeCollectionEntities({
            collectionNode: childCollectionNode,
            folder: input.folder,
            rootFolderKey: input.rootFolderKey,
            rootFolderSlug: input.rootFolderSlug,
            rootCollectionSlug: input.rootCollectionSlug,
            lineage: nextLineage,
            entities: input.entities,
        });
        for (const childEntity of childEntities) {
            addScopedEntityCandidates({
                ...input,
                entity: childEntity,
                collectionNode: childCollectionNode,
                lineage: nextLineage,
                visitedCollectionNodeKeys,
            });
        }
    }
}
function findCollectionContextForEntity(input) {
    const parentEntityByAlias = new Map((input.parentEntities ?? []).map((parent) => [parent.alias, parent]));
    const parentValues = Object.fromEntries((input.parentEntities ?? []).map((parent) => [
        parent.alias,
        {
            entityTypeKey: parent.entityTypeKey,
            entityId: parent.entityId,
            entitySlug: parent.entitySlug,
        },
    ]));
    const matchingNodes = input.collectionNodes.filter((node) => {
        if (node.entityTypeKey !== input.entity._entityTypeKey) {
            return false;
        }
        if (node.fromParent) {
            const parent = parentEntityByAlias.get(node.fromParent.alias);
            if (!parent || parent.entityTypeKey !== node.fromParent.entityTypeKey) {
                return false;
            }
        }
        return evaluateCollectionMembership({
            entity: input.entity,
            filter: node.filter,
            filterFnKey: node.filterFnKey,
            parentValues,
            pathScope: {
                rootFolderKey: node.rootFolderKey,
                rootFolderSlug: input.rootFolderSlug,
                folderNodeKeys: [node.key],
                folderSlugs: [node.slug],
                entityLineage: [],
            },
        });
    });
    const collectionNode = [...matchingNodes].sort((left, right) => {
        const specificityDelta = getCollectionNodeSpecificity(right) - getCollectionNodeSpecificity(left);
        if (specificityDelta !== 0) {
            return specificityDelta;
        }
        return left.listOrder - right.listOrder;
    })[0];
    if (!collectionNode) {
        return undefined;
    }
    return {
        slug: collectionNode.slug,
        key: collectionNode.key,
        parentEntities: input.parentEntities,
    };
}
function addRelationOccurrencesForEntity(input) {
    const typeKey = input.entity._entityTypeKey;
    const relationLineage = input.entityLineage.map((entry) => ({
        entityTypeKey: entry.entityTypeKey,
        entitySlug: entry.entitySlug,
    }));
    recordOccurrencePath({
        index: input.index,
        entityId: input.entity._id,
        entityTypeKey: typeKey,
        entitySlug: input.entity.slug,
        path: resolveEntityRouteFromTreeSelection({
            rootFolderSlug: input.rootFolderSlug,
            entityTypeKey: typeKey,
            entitySlug: input.entity.slug,
            aliases: input.aliases,
            entityLineage: relationLineage,
        }),
        order: input.orderRef.current++,
        rootFolderKey: input.rootFolderKey,
        rootFolderSlug: input.rootFolderSlug,
        folderSlugs: [],
        lineage: relationLineage,
    });
    const allowedChildTypes = input.topologyChildren.get(typeKey) || [];
    const relationChildren = input.childrenByParent.get(input.entity._id) || [];
    const allowedChildren = relationChildren
        .filter((child) => allowedChildTypes.includes(child.childTypeKey))
        .sort((left, right) => {
        const sortDelta = (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
        if (sortDelta !== 0)
            return sortDelta;
        return left.childEntityId.localeCompare(right.childEntityId);
    });
    const nextLineage = [
        ...input.entityLineage,
        {
            alias: getRequiredEntityAlias(input.aliases, typeKey),
            entityTypeKey: typeKey,
            entityId: input.entity._id,
            entitySlug: input.entity.slug,
        },
    ];
    for (const relationChild of allowedChildren) {
        const childEntity = input.entityById.get(relationChild.childEntityId);
        if (!childEntity) {
            continue;
        }
        addRelationOccurrencesForEntity({
            ...input,
            entity: childEntity,
            entityLineage: nextLineage,
            collectionContext: findCollectionContextForEntity({
                entity: childEntity,
                rootFolderSlug: input.rootFolderSlug,
                collectionNodes: input.collectionNodes,
                parentEntities: nextLineage,
            }),
        });
    }
}
function addConfiguredRootSingletonOccurrences(input) {
    const rootNode = input.rootNode;
    if (!rootNode) {
        return;
    }
    const rootFolderSlug = input.rootFolder.slug || input.rootFolder.key;
    const singletonNodes = input.folderNodes
        .filter((node) => node.kind === "singleton" &&
        node.parentKey === rootNode.key &&
        node.rootFolderKey === input.rootFolder.key &&
        Boolean(node.entityTypeKey))
        .sort((left, right) => left.listOrder - right.listOrder);
    for (const singletonNode of singletonNodes) {
        const entity = input.entities.find((candidate) => candidate._entityTypeKey === singletonNode.entityTypeKey);
        if (!entity) {
            continue;
        }
        recordOccurrencePath({
            index: input.index,
            entityId: entity._id,
            entityTypeKey: entity._entityTypeKey,
            entitySlug: entity.slug,
            path: resolveEntityOccurrencePath({
                rootFolderSlug,
                entityTypeKey: entity._entityTypeKey,
                entitySlug: entity.slug,
                aliases: input.aliases,
            }),
            order: input.orderRef.current++,
            rootFolderKey: input.rootFolder.key,
            rootFolderSlug,
            folderSlugs: [],
            lineage: [],
        });
    }
}
function addRelationRootFolderOccurrences(input, index, orderRef) {
    if (!input.structure) {
        return;
    }
    const folderNodes = Array.isArray(input.structure.folderNodes) ? input.structure.folderNodes : [];
    const relations = input.relations ?? [];
    const frameworkTopologyRules = Array.isArray(input.structure.topologyRules)
        ? input.structure.topologyRules
        : [];
    const childrenByParent = new Map();
    for (const relation of relations) {
        const entries = childrenByParent.get(relation.parentEntityId) || [];
        entries.push({
            childEntityId: relation.childEntityId,
            childTypeKey: relation.childTypeKey,
            sortOrder: relation.sortOrder,
        });
        childrenByParent.set(relation.parentEntityId, entries);
    }
    const entityById = new Map();
    for (const entity of input.entities ?? []) {
        entityById.set(entity._id, entity);
    }
    const topologyChildren = new Map();
    for (const rule of frameworkTopologyRules) {
        const entries = topologyChildren.get(rule.parent) || [];
        entries.push(rule.child);
        topologyChildren.set(rule.parent, entries);
    }
    const sortedRootFolders = [...(input.structure.rootFolders ?? [])].sort((left, right) => left.listOrder - right.listOrder);
    for (const rootFolder of sortedRootFolders) {
        const rootNode = folderNodes.find((node) => node.kind === "root" && node.rootFolderKey === rootFolder.key);
        if (rootNode?.settings?.projectTreeFolders?.enabled) {
            continue;
        }
        addConfiguredRootSingletonOccurrences({
            index,
            orderRef,
            rootFolder,
            rootNode,
            folderNodes,
            entities: [...entityById.values()],
            aliases: input.aliases,
        });
        const allCollectionNodesForRoot = folderNodes.filter((node) => node.kind === "collection" && node.rootFolderKey === rootFolder.key);
        const rootNodeKeys = new Set(folderNodes
            .filter((node) => node.kind === "root" && node.rootFolderKey === rootFolder.key)
            .map((node) => node.key));
        const rootCollectionNodes = allCollectionNodesForRoot
            .filter((node) => !!node.parentKey && rootNodeKeys.has(node.parentKey))
            .sort((left, right) => left.listOrder - right.listOrder);
        const rootCollectionNodesByEntityType = new Map();
        for (const node of rootCollectionNodes) {
            if (!node.entityTypeKey)
                continue;
            const entries = rootCollectionNodesByEntityType.get(node.entityTypeKey) || [];
            entries.push(node);
            rootCollectionNodesByEntityType.set(node.entityTypeKey, entries);
        }
        const rootFolderEntityTypeKeys = rootCollectionNodesByEntityType.size > 0
            ? Array.from(rootCollectionNodesByEntityType.keys())
            : getRootFolderEntityTypeKeys(input.structure, rootFolder.key);
        const rootFolderChildTypes = new Set();
        for (const rule of frameworkTopologyRules) {
            if (rootFolderEntityTypeKeys.includes(rule.parent) &&
                rootFolderEntityTypeKeys.includes(rule.child)) {
                rootFolderChildTypes.add(rule.child);
            }
        }
        const rootTypes = rootFolderEntityTypeKeys.filter((typeKey) => !rootFolderChildTypes.has(typeKey));
        const rootEntities = [];
        const rootFolderSlug = rootFolder.slug || rootFolder.key;
        for (const [, entity] of entityById) {
            if (!rootTypes.includes(entity._entityTypeKey)) {
                continue;
            }
            const collectionNodes = rootCollectionNodesByEntityType.get(entity._entityTypeKey) || [];
            if (collectionNodes.length === 0) {
                rootEntities.push(entity);
                continue;
            }
            const included = collectionNodes.some((collectionNode) => evaluateCollectionMembership({
                entity: entity,
                filter: collectionNode.filter,
                filterFnKey: collectionNode.filterFnKey,
                pathScope: {
                    rootFolderKey: rootFolder.key,
                    rootFolderSlug,
                    folderNodeKeys: [collectionNode.key],
                    folderSlugs: [collectionNode.slug],
                    entityLineage: [],
                },
            }));
            if (included) {
                rootEntities.push(entity);
            }
        }
        const sortEntities = (entities) => [...entities].sort((left, right) => (left.name ?? left.slug).localeCompare(right.name ?? right.slug) ||
            left.slug.localeCompare(right.slug));
        if (rootTypes.length <= 1) {
            for (const entity of sortEntities(rootEntities)) {
                addRelationOccurrencesForEntity({
                    index,
                    orderRef,
                    entity,
                    entityById,
                    childrenByParent,
                    topologyChildren,
                    rootFolderKey: rootFolder.key,
                    rootFolderSlug,
                    collectionNodes: allCollectionNodesForRoot,
                    aliases: input.aliases,
                    entityLineage: [],
                });
            }
            continue;
        }
        const rootEntitiesByType = new Map();
        for (const entity of rootEntities) {
            const entries = rootEntitiesByType.get(entity._entityTypeKey) || [];
            entries.push(entity);
            rootEntitiesByType.set(entity._entityTypeKey, entries);
        }
        for (const entityTypeKey of rootTypes) {
            const entitiesForType = sortEntities(rootEntitiesByType.get(entityTypeKey) || []);
            const rootCollectionNode = [
                ...(rootCollectionNodesByEntityType.get(entityTypeKey) ?? []),
            ].sort((left, right) => left.listOrder - right.listOrder)[0];
            for (const entity of entitiesForType) {
                addRelationOccurrencesForEntity({
                    index,
                    orderRef,
                    entity,
                    entityById,
                    childrenByParent,
                    topologyChildren,
                    rootFolderKey: rootFolder.key,
                    rootFolderSlug,
                    collectionNodes: allCollectionNodesForRoot,
                    aliases: input.aliases,
                    entityLineage: [],
                    collectionContext: rootCollectionNode
                        ? {
                            slug: rootCollectionNode.slug,
                            key: rootCollectionNode.key,
                        }
                        : undefined,
                });
            }
        }
    }
}
function addScopedProjectTreeOccurrences(input, index, orderRef) {
    if (!input.structure || !input.projectTreeFolders?.length || !input.entities?.length) {
        return;
    }
    const folderNodes = Array.isArray(input.structure.folderNodes) ? input.structure.folderNodes : [];
    const sortedRootFolders = [...(input.structure.rootFolders ?? [])].sort((left, right) => left.listOrder - right.listOrder);
    for (const rootFolder of sortedRootFolders) {
        const rootNode = folderNodes.find((node) => node.kind === "root" &&
            node.rootFolderKey === rootFolder.key &&
            node.settings?.projectTreeFolders?.enabled === true);
        if (!rootNode?.settings?.projectTreeFolders?.kind) {
            continue;
        }
        const projectTreeFolders = [...input.projectTreeFolders]
            .filter((folder) => folder.kind === rootNode.settings?.projectTreeFolders?.kind &&
            folder.parentFrameworkNodeKey === rootNode.key)
            .sort((left, right) => {
            const leftName = left.name ?? left.slug ?? "";
            const rightName = right.name ?? right.slug ?? "";
            const byName = leftName.localeCompare(rightName);
            if (byName !== 0)
                return byName;
            return (left.slug ?? "").localeCompare(right.slug ?? "");
        });
        const collectionNodes = folderNodes
            .filter((node) => node.kind === "collection" && node.rootFolderKey === rootFolder.key)
            .sort((left, right) => left.listOrder - right.listOrder);
        const topLevelCollectionNodes = collectionNodes.filter((node) => node.parentKey === rootNode.key);
        const rootFolderSlug = rootFolder.slug || rootFolder.key;
        for (const folder of projectTreeFolders) {
            for (const collectionNode of topLevelCollectionNodes) {
                const entities = resolveProjectTreeCollectionEntities({
                    collectionNode,
                    folder,
                    rootFolderKey: rootFolder.key,
                    rootFolderSlug,
                    rootCollectionSlug: collectionNode.slug,
                    lineage: [],
                    entities: input.entities,
                });
                for (const entity of entities) {
                    addScopedEntityCandidates({
                        index,
                        orderRef,
                        entity,
                        folder,
                        collectionNode,
                        rootFolderKey: rootFolder.key,
                        rootFolderSlug,
                        rootCollectionSlug: collectionNode.slug,
                        aliases: input.aliases,
                        collectionNodes,
                        entities: input.entities,
                        lineage: [],
                    });
                }
            }
        }
    }
}
function buildEntityDisplayByType(structure) {
    return new Map((structure?.entityDisplays ?? []).map((display) => [display.entityTypeKey, display]));
}
function buildEntityOccurrenceNodeId(entityId, occurrencePath) {
    return `entity:${entityId}:${occurrencePath}`;
}
function resolveCollectionParentEntities(input) {
    if (!input.collectionNode.fromParent) {
        return undefined;
    }
    const match = [...input.lineage]
        .reverse()
        .find((entry) => entry.entity._entityTypeKey === input.collectionNode.fromParent?.entityTypeKey);
    const parents = match
        ? [
            {
                alias: input.collectionNode.fromParent.alias,
                entityTypeKey: match.entity._entityTypeKey,
                entityId: match.entity._id,
                entitySlug: match.entity.slug,
            },
        ]
        : [];
    return parents.length > 0 ? parents : undefined;
}
function resolveConfiguredCollectionEntities(input) {
    const entityTypeKey = input.collectionNode.entityTypeKey;
    if (!entityTypeKey) {
        return [];
    }
    const lineageForParents = input.lineage.map((entry) => ({
        entityTypeKey: entry.entity._entityTypeKey,
        entityId: entry.entity._id,
        entitySlug: entry.entity.slug,
    }));
    const { parentValues, missingRequired } = resolveParentValues(input.collectionNode.fromParent, lineageForParents);
    if (missingRequired.length > 0) {
        return [];
    }
    const matching = input.entities.filter((entity) => {
        if (entity._entityTypeKey !== entityTypeKey) {
            return false;
        }
        return evaluateCollectionMembership({
            entity: entity,
            filter: input.collectionNode.filter,
            filterFnKey: input.collectionNode.filterFnKey,
            parentValues,
            pathScope: {
                rootFolderKey: input.rootFolderKey,
                rootFolderSlug: input.rootFolderSlug,
                folderNodeKeys: [
                    ...input.lineage.map((entry) => entry.collectionNodeKey),
                    input.collectionNode.key,
                ],
                folderSlugs: input.folderSlugs,
                entityLineage: input.lineage.map((entry) => ({
                    entityTypeKey: entry.entity._entityTypeKey,
                    entityId: entry.entity._id,
                    entitySlug: entry.entity.slug,
                    nodeKey: entry.collectionNodeKey,
                })),
            },
        });
    });
    return matching;
}
export function resolveFrameworkCollectionEntities(input) {
    return resolveConfiguredCollectionEntities({
        collectionNode: input.collectionNode,
        rootFolderKey: input.rootFolderKey,
        rootFolderSlug: input.rootFolderSlug,
        folderSlugs: input.folderSlugs,
        lineage: [],
        entities: input.entities,
    });
}
export function resolveFrameworkProjectTreeCollectionSections(input) {
    const collectionNodes = [...(input.structure?.folderNodes ?? [])]
        .filter((node) => node.kind === "collection" &&
        node.rootFolderSlug === input.rootFolderSlug &&
        node.parentKey === input.parentFrameworkNodeKey)
        .sort((left, right) => left.listOrder - right.listOrder);
    return collectionNodes.map((node) => ({
        node,
        entities: resolveProjectTreeCollectionEntities({
            collectionNode: node,
            folder: {
                _id: input.projectTreeFolderId,
                slug: input.projectTreeFolderSlug,
                kind: "",
                parentFrameworkNodeKey: input.parentFrameworkNodeKey,
                sortOrder: 0,
            },
            rootFolderKey: node.rootFolderKey,
            rootFolderSlug: input.rootFolderSlug,
            rootCollectionSlug: node.slug,
            lineage: [],
            entities: input.entities,
        }),
    }));
}
export function resolveFrameworkFolderCollectionSections(input) {
    const collectionNodes = [...(input.structure?.folderNodes ?? [])]
        .filter((node) => node.kind === "collection" &&
        node.parentKey === input.parentNodeKey &&
        typeof node.entityTypeKey === "string")
        .sort((left, right) => left.listOrder - right.listOrder);
    return collectionNodes.map((node) => ({
        node,
        entities: resolveFrameworkCollectionEntities({
            collectionNode: node,
            rootFolderKey: input.rootFolderKey,
            rootFolderSlug: input.rootFolderSlug,
            folderSlugs: input.folderSlugs,
            entities: input.entities,
        }),
    }));
}
function buildNavigationCollectionNode(input) {
    return {
        id: input.id,
        label: input.label,
        type: "entity-collection",
        path: input.path,
        iconKey: input.iconKey,
        frameworkNodeKey: input.frameworkNodeKey,
        frameworkNodeKind: input.frameworkNodeKey ? "collection" : undefined,
        rootFolderKey: input.rootFolderKey,
        rootFolderSlug: input.rootFolderSlug,
        collectionSlug: input.collectionSlug,
        collectionNodeKey: input.collectionNodeKey,
        entityTypeKey: input.entityTypeKey,
        projectTreeFolderId: input.projectTreeFolderId,
        parentEntities: input.parentEntities,
        children: input.children,
    };
}
function buildScopedProjectTreeEntityNode(input) {
    const entityDisplay = input.entityDisplayByType.get(input.entity._entityTypeKey);
    const parentEntities = resolveCollectionParentEntities({
        collectionNode: input.collectionNode,
        lineage: input.lineage,
    });
    const lineageForPath = input.lineage.map((entry) => ({
        entityTypeKey: entry.entity._entityTypeKey,
        entitySlug: entry.entity.slug,
    }));
    const occurrencePath = resolveEntityOccurrencePath({
        rootFolderSlug: input.rootFolderSlug,
        entityTypeKey: input.entity._entityTypeKey,
        entitySlug: input.entity.slug,
        aliases: input.aliases,
        folderSlugs: [input.folder.slug],
        entityLineage: lineageForPath,
    });
    const nextLineage = [
        ...input.lineage,
        { entity: input.entity, collectionNodeKey: input.collectionNode.key },
    ];
    const childNodes = input.collectionNodes
        .filter((node) => node.parentKey === input.collectionNode.key)
        .sort((left, right) => left.listOrder - right.listOrder)
        .flatMap((childCollectionNode) => {
        const childEntities = resolveProjectTreeCollectionEntities({
            collectionNode: childCollectionNode,
            folder: input.folder,
            rootFolderKey: input.rootFolderKey,
            rootFolderSlug: input.rootFolderSlug,
            rootCollectionSlug: input.rootCollectionSlug,
            lineage: nextLineage,
            entities: input.entities,
        });
        if (childEntities.length === 0 || !childCollectionNode.entityTypeKey) {
            return [];
        }
        return childEntities.map((childEntity) => buildScopedProjectTreeEntityNode({
            ...input,
            entity: childEntity,
            collectionNode: childCollectionNode,
            lineage: nextLineage,
        }));
    });
    return {
        id: buildEntityOccurrenceNodeId(input.entity._id, occurrencePath),
        label: input.entity.name ?? input.entity.slug,
        type: "entity",
        path: occurrencePath,
        iconKey: entityDisplay?.iconKey ?? null,
        frameworkNodeKey: input.collectionNode.key,
        frameworkNodeKind: "collection",
        rootFolderKey: input.rootFolderKey,
        rootFolderSlug: input.rootFolderSlug,
        entityTypeKey: input.entity._entityTypeKey,
        entityAlias: getRequiredEntityAlias(input.aliases, input.entity._entityTypeKey),
        entityId: input.entity._id,
        entitySlug: input.entity.slug,
        collectionSlug: input.collectionNode.slug,
        collectionNodeKey: input.collectionNode.key,
        projectTreeFolderId: input.entity.projectTreeFolderId ?? input.folder._id,
        parentEntities,
        children: childNodes.length > 0 ? childNodes : undefined,
    };
}
function buildProjectTreeRootNode(input) {
    const projectTreeRootNodeKey = input.rootNode.key;
    const projectTreeFolderKind = requireCompiledProjectTreeFolderKind({
        rootFolder: input.rootFolder,
        rootNode: input.rootNode,
    });
    const collectionNodes = [...input.collectionNodes].sort((left, right) => {
        const byOrder = left.listOrder - right.listOrder;
        if (byOrder !== 0)
            return byOrder;
        return left.label.localeCompare(right.label);
    });
    const topLevelCollectionNodes = collectionNodes.filter((node) => node.parentKey === projectTreeRootNodeKey);
    const projectTreeFolders = [...input.projectTreeFolders]
        .filter((folder) => folder.kind === projectTreeFolderKind &&
        folder.parentFrameworkNodeKey === projectTreeRootNodeKey)
        .sort((left, right) => {
        const leftName = left.name ?? left.slug ?? "";
        const rightName = right.name ?? right.slug ?? "";
        const byName = leftName.localeCompare(rightName);
        if (byName !== 0)
            return byName;
        return (left.slug ?? "").localeCompare(right.slug ?? "");
    });
    const rootFolderSlug = input.rootFolder.slug || input.rootFolder.key;
    return {
        id: input.rootFolder.key,
        label: input.rootFolder.label,
        type: "root-folder",
        path: `/${rootFolderSlug}`,
        iconKey: input.rootFolder.iconKey ?? null,
        frameworkNodeKey: input.rootNode.key,
        frameworkNodeKind: "root",
        rootFolderKey: input.rootFolder.key,
        rootFolderSlug,
        // Solution-model frameworks own folder creation on their hand-built root-folder landing
        // (Step 1 "New folder", or Claudia) — their sidebar offers no folder-create affordance and
        // no first-folder prompt row. Frameworks without such a landing keep the sidebar as their
        // folder-creation surface (the generic landing has none), and so does an unknown key.
        allowProjectTreeFolderCreate: !isSolutionModelFrameworkKey(input.frameworkKey),
        parentFrameworkNodeKey: projectTreeRootNodeKey,
        projectTreeFolderKind,
        children: projectTreeFolders.map((folder) => ({
            id: `projectTreeFolder:${folder._id}`,
            label: folder.name ?? folder.slug,
            type: "project-tree-folder",
            path: `/${rootFolderSlug}/${folder.slug}`,
            frameworkNodeKey: input.rootNode.key,
            frameworkNodeKind: "root",
            rootFolderKey: input.rootFolder.key,
            rootFolderSlug,
            projectTreeFolderId: folder._id,
            children: topLevelCollectionNodes
                .flatMap((collectionNode) => {
                const entities = resolveProjectTreeCollectionEntities({
                    collectionNode,
                    folder,
                    rootFolderKey: input.rootFolder.key,
                    rootFolderSlug,
                    rootCollectionSlug: collectionNode.slug,
                    lineage: [],
                    entities: input.entities,
                });
                if (entities.length === 0 || !collectionNode.entityTypeKey) {
                    return [];
                }
                return [
                    buildNavigationCollectionNode({
                        id: `projectTreeFolder:${folder._id}:${collectionNode.slug}`,
                        label: collectionNode.label,
                        iconKey: collectionNode.iconKey ?? null,
                        path: `/${rootFolderSlug}/${folder.slug}/${collectionNode.slug}`,
                        frameworkNodeKey: collectionNode.key,
                        rootFolderKey: input.rootFolder.key,
                        rootFolderSlug,
                        collectionSlug: collectionNode.slug,
                        collectionNodeKey: collectionNode.key,
                        entityTypeKey: collectionNode.entityTypeKey,
                        projectTreeFolderId: folder._id,
                        children: entities.map((entity) => buildScopedProjectTreeEntityNode({
                            entity,
                            folder,
                            collectionNode,
                            rootFolderKey: input.rootFolder.key,
                            rootFolderSlug,
                            rootCollectionSlug: collectionNode.slug,
                            aliases: input.aliases,
                            collectionNodes,
                            entities: input.entities,
                            entityDisplayByType: input.entityDisplayByType,
                            lineage: [],
                        })),
                    }),
                ];
            })
                .filter((node) => (node.children?.length ?? 0) > 0),
        })),
    };
}
function buildConfiguredCollectionEntityNode(input) {
    const entityDisplay = input.entityDisplayByType.get(input.entity._entityTypeKey);
    const parentEntities = resolveCollectionParentEntities({
        collectionNode: input.collectionNode,
        lineage: input.lineage,
    });
    const lineageForPath = input.lineage.map((entry) => ({
        entityTypeKey: entry.entity._entityTypeKey,
        entitySlug: entry.entity.slug,
    }));
    const occurrencePath = resolveEntityOccurrencePath({
        rootFolderSlug: input.rootFolderSlug,
        entityTypeKey: input.entity._entityTypeKey,
        entitySlug: input.entity.slug,
        aliases: input.aliases,
        folderSlugs: input.folderSlugs,
        entityLineage: lineageForPath,
    });
    const nextLineage = [
        ...input.lineage,
        { entity: input.entity, collectionNodeKey: input.collectionNode.key },
    ];
    const childNodes = input.collectionNodes
        .filter((node) => node.parentKey === input.collectionNode.key)
        .sort((left, right) => left.listOrder - right.listOrder)
        .flatMap((childCollectionNode) => {
        const childEntities = resolveConfiguredCollectionEntities({
            collectionNode: childCollectionNode,
            rootFolderKey: input.rootFolderKey,
            rootFolderSlug: input.rootFolderSlug,
            folderSlugs: input.folderSlugs,
            lineage: nextLineage,
            entities: input.entities,
        });
        if (childEntities.length === 0 || !childCollectionNode.entityTypeKey) {
            return [];
        }
        return childEntities.map((childEntity) => buildConfiguredCollectionEntityNode({
            ...input,
            entity: childEntity,
            collectionNode: childCollectionNode,
            lineage: nextLineage,
        }));
    });
    return {
        id: buildEntityOccurrenceNodeId(input.entity._id, occurrencePath),
        label: input.entity.name ?? input.entity.slug,
        type: "entity",
        path: occurrencePath,
        iconKey: entityDisplay?.iconKey ?? null,
        frameworkNodeKey: input.collectionNode.key,
        frameworkNodeKind: "collection",
        rootFolderKey: input.rootFolderKey,
        rootFolderSlug: input.rootFolderSlug,
        entityTypeKey: input.entity._entityTypeKey,
        entityAlias: getRequiredEntityAlias(input.aliases, input.entity._entityTypeKey),
        entityId: input.entity._id,
        entitySlug: input.entity.slug,
        collectionSlug: input.collectionNode.slug,
        collectionNodeKey: input.collectionNode.key,
        parentEntities,
        children: childNodes.length > 0 ? childNodes : undefined,
    };
}
function buildRelationEntityNode(input) {
    const typeKey = input.entity._entityTypeKey;
    const entityDisplay = input.entityDisplayByType.get(typeKey);
    const entityAlias = getRequiredEntityAlias(input.aliases, typeKey);
    const allowedChildTypes = input.topologyChildren.get(typeKey) || [];
    const resolvedCollectionContext = input.collectionContext ??
        findCollectionContextForEntity({
            entity: input.entity,
            rootFolderSlug: input.rootFolderSlug,
            collectionNodes: input.collectionNodes,
            parentEntities: input.entityLineage,
        });
    const occurrencePath = resolveEntityOccurrencePath({
        rootFolderSlug: input.rootFolderSlug,
        entityTypeKey: typeKey,
        entitySlug: input.entity.slug,
        aliases: input.aliases,
        folderSlugs: input.folderSlugs,
        entityLineage: (input.entityLineage ?? []).map((parent) => ({
            entityTypeKey: parent.entityTypeKey,
            entityAlias: parent.alias,
            entitySlug: parent.entitySlug,
        })),
    });
    const relationChildren = input.childrenByParent.get(input.entity._id) || [];
    const allowedChildren = relationChildren
        .filter((child) => allowedChildTypes.includes(child.childTypeKey))
        .sort((left, right) => {
        const sortDelta = (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
        if (sortDelta !== 0) {
            return sortDelta;
        }
        return left.childEntityId.localeCompare(right.childEntityId);
    });
    const childNodes = [];
    for (const relationChild of allowedChildren) {
        const childEntity = input.entityById.get(relationChild.childEntityId);
        if (!childEntity) {
            continue;
        }
        const nextEntityLineage = [
            ...(input.entityLineage ?? []),
            {
                alias: entityAlias,
                entityTypeKey: typeKey,
                entityId: input.entity._id,
                entitySlug: input.entity.slug,
            },
        ];
        const childCollectionContext = findCollectionContextForEntity({
            entity: childEntity,
            rootFolderSlug: input.rootFolderSlug,
            collectionNodes: input.collectionNodes,
            parentEntities: nextEntityLineage,
        });
        childNodes.push(buildRelationEntityNode({
            ...input,
            entity: childEntity,
            collectionContext: childCollectionContext,
            entityLineage: nextEntityLineage,
        }));
    }
    return {
        id: buildEntityOccurrenceNodeId(input.entity._id, occurrencePath),
        label: input.entity.name ?? input.entity.slug,
        type: "entity",
        path: occurrencePath,
        iconKey: entityDisplay?.iconKey ?? null,
        frameworkNodeKey: resolvedCollectionContext?.key,
        frameworkNodeKind: resolvedCollectionContext?.key ? "collection" : undefined,
        rootFolderKey: input.rootFolderKey,
        rootFolderSlug: input.rootFolderSlug,
        entityTypeKey: typeKey,
        entityAlias,
        entityId: input.entity._id,
        entitySlug: input.entity.slug,
        collectionSlug: resolvedCollectionContext?.slug,
        collectionNodeKey: resolvedCollectionContext?.key,
        parentEntities: resolvedCollectionContext?.parentEntities ?? input.entityLineage,
        children: childNodes.length > 0 ? childNodes : undefined,
    };
}
function buildConfiguredRootFolderChildren(input) {
    return input.rootChildNodes.flatMap((node) => {
        if (node.kind === "singleton" && node.entityTypeKey) {
            const entity = input.allEntities.find((candidate) => candidate._entityTypeKey === node.entityTypeKey);
            if (!entity) {
                return [];
            }
            return [
                buildRelationEntityNode({
                    entity,
                    entityById: new Map(input.allEntities.map((candidate) => [candidate._id, candidate])),
                    childrenByParent: new Map(),
                    topologyChildren: new Map(),
                    rootFolderKey: input.rootFolderKey,
                    rootFolderSlug: input.rootFolderSlug,
                    collectionNodes: input.collectionNodes,
                    aliases: input.aliases,
                    entityDisplayByType: input.entityDisplayByType,
                }),
            ];
        }
        if (node.kind === "collection" && node.entityTypeKey) {
            const entities = resolveConfiguredCollectionEntities({
                collectionNode: node,
                rootFolderKey: input.rootFolderKey,
                rootFolderSlug: input.rootFolderSlug,
                folderSlugs: [],
                lineage: [],
                entities: input.allEntities,
            });
            if (entities.length === 0) {
                return [];
            }
            return [
                buildNavigationCollectionNode({
                    id: `${input.rootFolderKey}::${node.key}`,
                    label: node.label,
                    iconKey: node.iconKey ?? null,
                    path: `/${input.rootFolderSlug}/${node.slug}`,
                    frameworkNodeKey: node.key,
                    rootFolderKey: input.rootFolderKey,
                    rootFolderSlug: input.rootFolderSlug,
                    collectionSlug: node.slug,
                    collectionNodeKey: node.key,
                    entityTypeKey: node.entityTypeKey,
                    children: entities.map((entity) => buildConfiguredCollectionEntityNode({
                        entity,
                        collectionNode: node,
                        rootFolderKey: input.rootFolderKey,
                        rootFolderSlug: input.rootFolderSlug,
                        folderSlugs: [],
                        aliases: input.aliases,
                        collectionNodes: input.collectionNodes,
                        entities: input.allEntities,
                        entityDisplayByType: input.entityDisplayByType,
                        lineage: [],
                    })),
                }),
            ];
        }
        if (node.kind !== "folder") {
            return [];
        }
        const folderSlugs = [node.slug];
        const childCollectionNodes = input.collectionNodes
            .filter((candidate) => candidate.parentKey === node.key)
            .sort((left, right) => left.listOrder - right.listOrder);
        const children = node.settings?.displayMode === "mixed"
            ? childCollectionNodes.flatMap((childCollectionNode) => {
                const entities = resolveConfiguredCollectionEntities({
                    collectionNode: childCollectionNode,
                    rootFolderKey: input.rootFolderKey,
                    rootFolderSlug: input.rootFolderSlug,
                    folderSlugs,
                    lineage: [],
                    entities: input.allEntities,
                });
                return entities.map((entity) => buildConfiguredCollectionEntityNode({
                    entity,
                    collectionNode: childCollectionNode,
                    rootFolderKey: input.rootFolderKey,
                    rootFolderSlug: input.rootFolderSlug,
                    folderSlugs,
                    aliases: input.aliases,
                    collectionNodes: input.collectionNodes,
                    entities: input.allEntities,
                    entityDisplayByType: input.entityDisplayByType,
                    lineage: [],
                }));
            })
            : [];
        return [
            {
                id: `${input.rootFolderKey}::${node.key}`,
                label: node.label,
                type: "project-tree-folder",
                path: `/${input.rootFolderSlug}/${node.slug}`,
                iconKey: node.iconKey ?? null,
                frameworkNodeKey: node.key,
                frameworkNodeKind: node.kind,
                rootFolderKey: input.rootFolderKey,
                rootFolderSlug: input.rootFolderSlug,
                children: children.length > 0 ? children : undefined,
            },
        ];
    });
}
function buildRootFolderChildren(input) {
    const rootFolderKey = input.rootFolder.key;
    const rootFolderSlug = input.rootFolder.slug || input.rootFolder.key;
    if (input.rootNode &&
        input.rootChildNodes.some((node) => node.kind === "singleton" || node.kind === "folder")) {
        return buildConfiguredRootFolderChildren({
            rootFolderKey,
            rootFolderSlug,
            rootNode: input.rootNode,
            rootChildNodes: input.rootChildNodes,
            allEntities: input.allEntities,
            collectionNodes: input.collectionNodes,
            aliases: input.aliases,
            entityDisplayByType: input.entityDisplayByType,
        });
    }
    const rootEntitiesByType = new Map();
    for (const entity of input.rootEntities) {
        const entitiesForType = rootEntitiesByType.get(entity._entityTypeKey) || [];
        entitiesForType.push(entity);
        rootEntitiesByType.set(entity._entityTypeKey, entitiesForType);
    }
    const sortEntities = (entities) => [...entities].sort((left, right) => (left.name ?? left.slug).localeCompare(right.name ?? right.slug) ||
        left.slug.localeCompare(right.slug));
    if (input.rootTypes.length <= 1) {
        return sortEntities(input.rootEntities).map((entity) => buildRelationEntityNode({
            entity,
            entityById: input.entityById,
            childrenByParent: input.childrenByParent,
            topologyChildren: input.topologyChildren,
            rootFolderKey,
            rootFolderSlug,
            collectionNodes: input.collectionNodes,
            aliases: input.aliases,
            entityDisplayByType: input.entityDisplayByType,
        }));
    }
    return input.rootTypes.map((entityTypeKey) => {
        const entitiesForType = sortEntities(rootEntitiesByType.get(entityTypeKey) || []);
        const entityDisplay = input.entityDisplayByType.get(entityTypeKey);
        const entityAlias = getRequiredEntityAlias(input.aliases, entityTypeKey);
        const rootCollectionNode = [
            ...(input.rootCollectionNodesByEntityType.get(entityTypeKey) ?? []),
        ].sort((left, right) => left.listOrder - right.listOrder)[0];
        const collectionSlug = rootCollectionNode?.slug;
        const collectionNodeKey = rootCollectionNode?.key;
        return buildNavigationCollectionNode({
            id: `${rootFolderKey}::${entityTypeKey}::collection`,
            label: entityDisplay?.pluralLabel ?? `${entityAlias}s`,
            iconKey: rootCollectionNode?.iconKey ?? entityDisplay?.iconKey ?? null,
            path: collectionSlug ? `/${rootFolderSlug}/${collectionSlug}` : undefined,
            frameworkNodeKey: collectionNodeKey,
            rootFolderKey,
            rootFolderSlug,
            collectionSlug: collectionSlug ?? "",
            collectionNodeKey: collectionNodeKey ?? "",
            entityTypeKey,
            children: entitiesForType.map((entity) => buildRelationEntityNode({
                entity,
                entityById: input.entityById,
                childrenByParent: input.childrenByParent,
                topologyChildren: input.topologyChildren,
                rootFolderKey,
                rootFolderSlug,
                collectionNodes: input.collectionNodes,
                aliases: input.aliases,
                entityDisplayByType: input.entityDisplayByType,
                collectionContext: collectionSlug && collectionNodeKey
                    ? {
                        slug: collectionSlug,
                        key: collectionNodeKey,
                    }
                    : undefined,
            })),
        });
    });
}
export function buildProjectNavigationTree(input) {
    if (!input.structure) {
        return { rootFolders: [] };
    }
    const entityDisplayByType = buildEntityDisplayByType(input.structure);
    const folderNodes = Array.isArray(input.structure.folderNodes) ? input.structure.folderNodes : [];
    const frameworkTopologyRules = Array.isArray(input.structure.topologyRules)
        ? input.structure.topologyRules
        : [];
    const childrenByParent = new Map();
    for (const relation of input.relations ?? []) {
        const children = childrenByParent.get(relation.parentEntityId) || [];
        children.push({
            childEntityId: relation.childEntityId,
            childTypeKey: relation.childTypeKey,
            sortOrder: relation.sortOrder,
        });
        childrenByParent.set(relation.parentEntityId, children);
    }
    const allEntities = [...(input.entities ?? [])];
    const entityById = new Map();
    for (const entity of allEntities) {
        entityById.set(entity._id, entity);
    }
    const topologyChildren = new Map();
    for (const rule of frameworkTopologyRules) {
        const children = topologyChildren.get(rule.parent) || [];
        children.push(rule.child);
        topologyChildren.set(rule.parent, children);
    }
    const sortedRootFolders = [...(input.structure.rootFolders ?? [])].sort((left, right) => left.listOrder - right.listOrder);
    const rootFolders = sortedRootFolders.map((rootFolder) => {
        const rootNode = folderNodes.find((node) => node.kind === "root" && node.rootFolderKey === rootFolder.key);
        if (rootNode?.settings?.projectTreeFolders?.enabled && input.projectTreeFolders) {
            return buildProjectTreeRootNode({
                rootFolder,
                frameworkKey: input.structure?.frameworkKey ?? null,
                rootNode,
                collectionNodes: folderNodes.filter((node) => node.kind === "collection" && node.rootFolderKey === rootFolder.key),
                projectTreeFolders: input.projectTreeFolders,
                entities: allEntities,
                aliases: input.aliases,
                entityDisplayByType,
            });
        }
        const collectionNodes = folderNodes.filter((node) => node.kind === "collection" && node.rootFolderKey === rootFolder.key);
        const rootNodeKeys = new Set(folderNodes
            .filter((node) => node.kind === "root" && node.rootFolderKey === rootFolder.key)
            .map((node) => node.key));
        const rootCollectionNodes = collectionNodes
            .filter((node) => !!node.parentKey && rootNodeKeys.has(node.parentKey))
            .sort((left, right) => left.listOrder - right.listOrder);
        const rootCollectionNodesByEntityType = new Map();
        for (const node of rootCollectionNodes) {
            if (!node.entityTypeKey)
                continue;
            const entries = rootCollectionNodesByEntityType.get(node.entityTypeKey) || [];
            entries.push(node);
            rootCollectionNodesByEntityType.set(node.entityTypeKey, entries);
        }
        const rootFolderEntityTypeKeys = rootCollectionNodesByEntityType.size > 0
            ? Array.from(rootCollectionNodesByEntityType.keys())
            : getRootFolderEntityTypeKeys(input.structure, rootFolder.key);
        const rootFolderChildTypes = new Set();
        for (const rule of frameworkTopologyRules) {
            if (rootFolderEntityTypeKeys.includes(rule.parent) &&
                rootFolderEntityTypeKeys.includes(rule.child)) {
                rootFolderChildTypes.add(rule.child);
            }
        }
        const rootTypes = rootFolderEntityTypeKeys.filter((typeKey) => !rootFolderChildTypes.has(typeKey));
        const rootEntities = [];
        const rootFolderSlug = rootFolder.slug || rootFolder.key;
        for (const entity of entityById.values()) {
            if (!rootTypes.includes(entity._entityTypeKey)) {
                continue;
            }
            const typedCollectionNodes = rootCollectionNodesByEntityType.get(entity._entityTypeKey) || [];
            if (typedCollectionNodes.length === 0) {
                rootEntities.push(entity);
                continue;
            }
            const included = typedCollectionNodes.some((collectionNode) => evaluateCollectionMembership({
                entity: entity,
                filter: collectionNode.filter,
                filterFnKey: collectionNode.filterFnKey,
                pathScope: {
                    rootFolderKey: rootFolder.key,
                    rootFolderSlug,
                    folderNodeKeys: [collectionNode.key],
                    folderSlugs: [collectionNode.slug],
                    entityLineage: [],
                },
            }));
            if (included) {
                rootEntities.push(entity);
            }
        }
        return {
            id: rootFolder.key,
            label: rootFolder.label,
            type: "root-folder",
            path: `/${rootFolderSlug}`,
            iconKey: rootFolder.iconKey ?? null,
            frameworkNodeKey: rootNode?.key,
            frameworkNodeKind: rootNode ? "root" : undefined,
            rootFolderKey: rootFolder.key,
            rootFolderSlug,
            children: buildRootFolderChildren({
                rootNode,
                rootFolder,
                rootChildNodes: folderNodes
                    .filter((node) => rootNode && node.parentKey === rootNode.key)
                    .sort((left, right) => left.listOrder - right.listOrder),
                collectionNodes,
                rootCollectionNodesByEntityType,
                rootTypes,
                rootEntities,
                allEntities,
                entityById,
                childrenByParent,
                topologyChildren,
                aliases: input.aliases,
                entityDisplayByType,
            }),
        };
    });
    return { rootFolders };
}
export function buildEntityNavigationIndex(input) {
    const index = {
        byEntityId: new Map(),
        byEntityKey: new Map(),
        occurrencesByEntityId: new Map(),
        occurrencesByEntityKey: new Map(),
    };
    const orderRef = { current: 0 };
    const canonicalRootFolderKeyByEntityType = getCanonicalRootFolderKeyByEntityType(input.structure);
    addRelationRootFolderOccurrences(input, index, orderRef);
    addScopedProjectTreeOccurrences(input, index, orderRef);
    finalizeCanonicalEntries(index, canonicalRootFolderKeyByEntityType);
    return index;
}
export function buildProjectNavigationModel(input) {
    return {
        entityIndex: buildEntityNavigationIndex(input),
    };
}
export function resolveCanonicalEntityPath(input) {
    const byId = typeof input.entityId === "string" && input.entityId.length > 0
        ? input.model.entityIndex.byEntityId.get(input.entityId)
        : null;
    if (byId) {
        return byId.path;
    }
    const byKey = input.model.entityIndex.byEntityKey.get(buildEntityNavigationKey(input.entityTypeKey, input.entitySlug));
    return byKey?.path ?? null;
}
export function listEntityNavigationOccurrences(input) {
    const byId = typeof input.entityId === "string" && input.entityId.length > 0
        ? input.model.entityIndex.occurrencesByEntityId.get(input.entityId)
        : null;
    if (byId) {
        return byId;
    }
    return (input.model.entityIndex.occurrencesByEntityKey.get(buildEntityNavigationKey(input.entityTypeKey, input.entitySlug)) ?? []);
}
export function resolveEntityOccurrencePath(input) {
    return resolveEntityRouteFromTreeSelection(input);
}
